import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { ChartContainer } from '@/components/dashboard/ChartContainer'
import { type LibraryBook, monthlyPace } from '@/lib/insights'

/** Books added vs books finished per month, last 12 months */
export function ReadingPaceChart({ books }: { books: LibraryBook[] }) {
  const { t, i18n } = useTranslation()

  const { data, added, finished } = useMemo(() => {
    const pace = monthlyPace(books, 12)
    const format = new Intl.DateTimeFormat(i18n.language, { month: 'short' })
    return {
      data: pace.map((p) => {
        const [y, m] = p.month.split('-').map(Number)
        return {
          label: format.format(new Date(y, m - 1, 1)).replace('.', ''),
          added: p.added,
          finished: p.finished,
        }
      }),
      added: pace.reduce((sum, p) => sum + p.added, 0),
      finished: pace.reduce((sum, p) => sum + p.finished, 0),
    }
  }, [books, i18n.language])

  const verdict =
    added === 0 && finished === 0
      ? t('insights.pace.noActivity')
      : added > finished
        ? t('insights.pace.buyingMore', { added, finished })
        : t('insights.pace.readingMore', { added, finished })

  return (
    <ChartContainer title={t('insights.pace.title')} description={verdict}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
          <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 12 }} />
          <YAxis allowDecimals={false} axisLine={false} tickLine={false} tick={{ fontSize: 12 }} />
          <Tooltip />
          <Legend wrapperStyle={{ fontSize: '12px' }} />
          <Bar
            dataKey="added"
            name={t('insights.pace.added')}
            fill="#F59E0B"
            radius={[4, 4, 0, 0]}
          />
          <Bar
            dataKey="finished"
            name={t('insights.pace.finished')}
            fill="#10B981"
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </ChartContainer>
  )
}
